import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { getDatabase, ref, get, remove, update } from 'firebase/database';
import Navbar from './Navbar';
import fc from '../firebaseConfig';

const db = getDatabase(fc);

const fetchProducts = async () => {
  const productsRef = ref(db, 'products');
  const snapshot = await get(productsRef);
  const products = [];
  if (snapshot.exists()) {
    snapshot.forEach((childSnapshot) => {
      products.push({ id: childSnapshot.key, ...childSnapshot.val() });
    });
  }
  return products;
};

const TableProducts = () => {
  const [products, setProducts] = useState([]);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [productToEdit, setProductToEdit] = useState({ name: '', quantity: '', type: '' });
  const [productToDelete, setProductToDelete] = useState(null);
  const [errors, setErrors] = useState({ name: false, quantity: false, type: false });

  const navigate = useNavigate(); // Initialize useNavigate

  useEffect(() => {
    // Redirect if not logged in
    if (!localStorage.getItem('LOGGED_IN')) {
      navigate('/'); // Redirect to home page
    }

    const getProducts = async () => {
      const productsList = await fetchProducts();
      setProducts(productsList);
    };
    getProducts();
  }, [navigate]); // Include navigate in the dependency array

  const handleSaveEdit = async () => {
    const { name, quantity, type } = productToEdit;

    // Validate fields
    if (!name || !quantity || !type || isNaN(quantity)) {
      setErrors({
        name: !name,
        quantity: !quantity || isNaN(quantity),
        type: !type,
      });
      return;
    }

    if (productToEdit.id) {
      await update(ref(db, `products/${productToEdit.id}`), { name, quantity, type });
      const productsList = await fetchProducts();
      setProducts(productsList);
      setOpenEditDialog(false);
    }
  };

  const openEditDialogHandler = (product) => {
    setProductToEdit(product);
    setOpenEditDialog(true);
    setErrors({ name: false, quantity: false, type: false });
  };

  const closeEditDialogHandler = () => {
    setOpenEditDialog(false);
    setProductToEdit({ name: '', quantity: '', type: '' });
  };

  const handleDelete = async () => {
    if (productToDelete) {
      await remove(ref(db, `products/${productToDelete.id}`));
      setOpenDeleteDialog(false);
      const productsList = await fetchProducts();
      setProducts(productsList);
    }
  };

  const openDeleteDialogHandler = (product) => {
    setProductToDelete(product);
    setOpenDeleteDialog(true);
  };

  const closeDeleteDialogHandler = () => {
    setOpenDeleteDialog(false);
    setProductToDelete(null);
  };

  // Function to check if the product quantity is low
  const checkLowStock = (product) => {
    const { quantity, type } = product;
    if (type === 'ml' && quantity <= 94) return true;
    if (type === 'l' && quantity <= 2) return true;
    if (type === 'g' && quantity <= 94) return true;
    if (type === 'kg' && quantity <= 2) return true;
    if (type === 'szt' && quantity <= 20) return true;
    return false;
  };

  return (
    <div>
      <Navbar />
      <TableContainer component={Paper} style={{ width: '45rem', margin: '60px auto' }}>
        <h2 style={{ marginLeft: '15px' }} className='header'>Tabela Produktów</h2>

        {/* Warning messages for low stock products */}
        {products
          .filter((product) => checkLowStock(product)) // Only show products with low stock
          .map((product) => (
            <Typography key={product.id} style={{ color: 'red', marginLeft: '15px' }}>
              Uwaga, "{product.name}" zostało {product.quantity}{product.type}! Należy dokupić i edytować!
            </Typography>
          ))}

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nazwa</TableCell>
              <TableCell>Ilość</TableCell>
              <TableCell>Typ</TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id}>
                <TableCell>{product.name}</TableCell>
                <TableCell>{product.quantity}</TableCell>
                <TableCell>{product.type}</TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => openEditDialogHandler(product)}
                    style={{ marginRight: '10px' }}
                  >
                    Edytuj
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => openDeleteDialogHandler(product)}
                  >
                    Usuń
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Product Dialog */}
      <Dialog open={openEditDialog} onClose={closeEditDialogHandler}>
        <DialogTitle>Edytuj Produkt</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nazwa"
            fullWidth
            value={productToEdit.name}
            onChange={(e) => setProductToEdit(prev => ({ ...prev, name: e.target.value }))} 
            error={errors.name}
            helperText={errors.name ? "Pole nie może być puste" : ""}
          />
          <TextField
            margin="dense"
            label="Ilość"
            fullWidth
            value={productToEdit.quantity}
            onChange={(e) => setProductToEdit(prev => ({ ...prev, quantity: e.target.value }))} 
            error={errors.quantity}
            helperText={errors.quantity ? "Pole musi być liczbą" : ""}
          />
          
          {/* Select for product type */}
          <FormControl fullWidth margin="dense" error={errors.type}>
            <InputLabel>Typ</InputLabel>
            <Select
              value={productToEdit.type}
              onChange={(e) => setProductToEdit(prev => ({ ...prev, type: e.target.value }))} 
              label="Typ"
            >
              <MenuItem value="ml">mililitrów (ml)</MenuItem>
              <MenuItem value="l">litrów (l)</MenuItem>
              <MenuItem value="g">gramów (g)</MenuItem>
              <MenuItem value="kg">kilogramów (kg)</MenuItem>
              <MenuItem value="szt">sztuk (szt)</MenuItem>
            </Select>
            {errors.type && <Typography color="error">Pole nie może być puste</Typography>}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialogHandler} color="primary">Anuluj</Button>
          <Button onClick={handleSaveEdit} color="primary">Zapisz</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={closeDeleteDialogHandler}>
        <DialogTitle>Potwierdzenie usunięcia</DialogTitle>
        <DialogContent>
          <p>Czy na pewno chcesz usunąć {productToDelete?.name}?</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialogHandler}>Anuluj</Button>
          <Button onClick={handleDelete} color="secondary">Usuń</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TableProducts;
